/**
 * Personal ("SIX") calendar events — shared client-side rules (2026-09-11).
 * Plan: BDR_Mobile plans/impl_personal_calendar_events_2026_09_11.md §6.9.
 *
 * Everything the three calendar surfaces (month / week / day) need to agree on
 * lives here so the filter, the all-day split and the month-dot merge can't
 * drift between CalendarPage, WeekModal and DayViewModal:
 *
 *  - The All | Personal | Work filter is applied at RENDER time (never a
 *    refetch) and persisted per browser under `bdr-calendar-event-filter`.
 *    Every localStorage touch is try/catch'd — a private window or a blocked
 *    storage API must never take the calendar down.
 *  - All-day items (`calendarItem.isAllDay === true`) are anchored by the
 *    server at Central NOON with no endMs. They are split out BEFORE lane
 *    layout (and dropped again inside computeLaneLayout as a belt-and-braces
 *    guard) so a noon anchor never paints as an hour block. [R2-G1]
 *  - Month summary rows carry personal items separately (`personalCount`,
 *    `personalUserColors`); a personal-only day reports `count: "0"`, so the
 *    badge/dots are re-derived per filter here. [R1-F26]
 */

export const EVENT_FILTER_ALL = "all";
export const EVENT_FILTER_PERSONAL = "personal";
export const EVENT_FILTER_WORK = "work";
export const EVENT_FILTERS = [EVENT_FILTER_ALL, EVENT_FILTER_PERSONAL, EVENT_FILTER_WORK];
export const EVENT_FILTER_STORAGE_KEY = "bdr-calendar-event-filter";

// Server contract: label trimmed, required, <= 80 chars (personalEvents.js).
export const PERSONAL_LABEL_MAX = 80;
// Used when the creator has no defaultColor (mirrors calendarLaneLayout).
export const PERSONAL_FALLBACK_COLOR = "#607D8B";

// ── item predicates ────────────────────────────────────────────────────────

export function isPersonalItem(item) {
  return (item?.calendarItem?.eventType || "job") === "personal";
}

export function isAllDayItem(item) {
  return item?.calendarItem?.isAllDay === true;
}

// ── persisted filter ───────────────────────────────────────────────────────

export function readStoredEventFilter() {
  try {
    const v = window.localStorage.getItem(EVENT_FILTER_STORAGE_KEY);
    return EVENT_FILTERS.includes(v) ? v : EVENT_FILTER_ALL;
  } catch {
    return EVENT_FILTER_ALL;
  }
}

export function writeStoredEventFilter(filter) {
  if (!EVENT_FILTERS.includes(filter)) return;
  try {
    window.localStorage.setItem(EVENT_FILTER_STORAGE_KEY, filter);
  } catch {
    // Storage unavailable — the in-memory state still drives the UI.
  }
}

// ── render-time filter ─────────────────────────────────────────────────────

/**
 * @param {Array} items  CalendarItemWithDetails[] as returned by getScheduleRange
 * @param {string} filter one of EVENT_FILTERS (anything else = "all")
 */
export function applyEventFilter(items, filter) {
  const list = Array.isArray(items) ? items : [];
  if (filter === EVENT_FILTER_PERSONAL) return list.filter(isPersonalItem);
  if (filter === EVENT_FILTER_WORK) return list.filter((i) => !isPersonalItem(i));
  return list;
}

// ── all-day split ──────────────────────────────────────────────────────────

const sameLocalDay = (ms, dayDate) => {
  const n = parseInt(ms, 10);
  if (Number.isNaN(n)) return false;
  const d = new Date(n);
  return (
    d.getFullYear() === dayDate.getFullYear() &&
    d.getMonth() === dayDate.getMonth() &&
    d.getDate() === dayDate.getDate()
  );
};

/** Two epoch-ms strings on the same LOCAL calendar day (component compare, never a delta). */
export function isSameLocalDay(msA, msB) {
  const b = toLocalDayStart(msB);
  return !!b && sameLocalDay(msA, b);
}

// Deterministic chip order: label (case-insensitive) then id. [R2-G7]
export function sortAllDayItems(items) {
  return [...items].sort((a, b) => {
    const la = (a?.calendarItem?.label || "").toLowerCase();
    const lb = (b?.calendarItem?.label || "").toLowerCase();
    if (la !== lb) return la < lb ? -1 : 1;
    const ia = String(a?.calendarItem?.id || "");
    const ib = String(b?.calendarItem?.id || "");
    return ia < ib ? -1 : ia > ib ? 1 : 0;
  });
}

/**
 * All-day items that land on `dayDate` (LOCAL calendar day of the noon anchor).
 * The anchor is Central noon, which is the same calendar day in every US zone,
 * so a local-day compare is the right key — never a clock compare.
 */
export function allDayItemsForDay(items, dayDate) {
  const list = Array.isArray(items) ? items : [];
  return sortAllDayItems(
    list.filter((it) => isAllDayItem(it) && sameLocalDay(it.calendarItem.startMs, dayDate))
  );
}

// ── month summary merge ────────────────────────────────────────────────────

/**
 * Re-derives a month cell's dots + count for the active filter.
 *   all      → userColors ∪ personalUserColors, count + personalCount
 *   personal → personalUserColors only, personalCount
 *   work     → userColors only, count
 * Never throws on a summary that predates the split (missing personal fields).
 */
export function mergeSummaryForFilter(summary, filter) {
  const work = parseInt(summary?.count, 10) || 0;
  const personal = parseInt(summary?.personalCount, 10) || 0;
  const workColors = Array.isArray(summary?.userColors) ? summary.userColors : [];
  const personalColors = Array.isArray(summary?.personalUserColors)
    ? summary.personalUserColors
    : [];

  if (filter === EVENT_FILTER_PERSONAL) {
    return { dots: personalColors, count: personal, workCount: 0, personalCount: personal };
  }
  if (filter === EVENT_FILTER_WORK) {
    return { dots: workColors, count: work, workCount: work, personalCount: 0 };
  }
  const dots = [...workColors];
  personalColors.forEach((c) => {
    if (!dots.includes(c)) dots.push(c);
  });
  return { dots, count: work + personal, workCount: work, personalCount: personal };
}

/** Badge text for a merged cell: "3 jobs" / "1 personal" / "4 events" (mixed). */
export function summaryCountLabel(merged) {
  const n = merged?.count || 0;
  const plural = n !== 1 ? "s" : "";
  if (merged.personalCount > 0 && merged.workCount === 0) return `${n} personal`;
  if (merged.personalCount > 0) return `${n} event${plural}`;
  return `${n} job${plural}`;
}

// ── time anchors ───────────────────────────────────────────────────────────

/** Local calendar day (00:00) of an epoch-ms string, or null. */
export function toLocalDayStart(msStr) {
  const n = parseInt(msStr, 10);
  if (Number.isNaN(n)) return null;
  const d = new Date(n);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * The all-day anchor the client sends: LOCAL NOON of the chosen date as an
 * epoch-ms string. The server re-normalizes to Central noon (no-op for a
 * Central client). DST-safe by construction (y/m/d/12 — never +Duration).
 */
export function toLocalNoonMsStr(msStr) {
  const day = toLocalDayStart(msStr);
  if (!day) return "";
  return String(
    new Date(day.getFullYear(), day.getMonth(), day.getDate(), 12, 0, 0).getTime()
  );
}

/** `dayDate` at hour:minute local, as an epoch-ms string. */
export function atLocalTimeMsStr(dayDate, hour24, minute) {
  return String(
    new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate(), hour24, minute, 0).getTime()
  );
}

/** Re-anchors the clock time of `msStr` onto `dayDate` (used when the date changes). */
export function moveClockToDayMsStr(msStr, dayDate) {
  const n = parseInt(msStr, 10);
  if (Number.isNaN(n)) return "";
  const d = new Date(n);
  return atLocalTimeMsStr(dayDate, d.getHours(), d.getMinutes());
}

// ── Repeat rules (2026-09-20) ──────────────────────────────────────────────
//
// The server owns every hard question here: which occurrences exist, how a
// pattern moves when the anchor moves, and what a skipped day maps to. This
// module carries only what the two modals need to agree on — the wire
// vocabulary and the shape of the form state.

export const SCOPE_OCCURRENCE = "occurrence";
export const SCOPE_FOLLOWING = "following";
export const SCOPE_SERIES = "series";

export const FREQ_WEEKLY = "weekly";
export const FREQ_MONTHLY = "monthly";
/** Wire sentinel that CLEARS a rule. `null` means "not provided". */
export const FREQ_NONE = "none";

export const WEEKDAY_NAMES = [
  "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",
];

/** True when this item is one occurrence of a repeating series. */
export function isSeriesOccurrence(item) {
  return !!item?.calendarItem?.seriesId;
}

/** True when the item carries a repeat rule at all (series or broken rule). */
export function hasRepeatRule(item) {
  return !!item?.calendarItem?.recurrence?.freq;
}

/** True when this occurrence carries its own details (a "Only this event" save, 2026-09-25). */
export function isOccurrenceOverridden(item) {
  return item?.calendarItem?.occurrenceOverridden === true;
}

/** How many occurrences of this series carry their own details. */
export function overrideCount(item) {
  const o = item?.calendarItem?.recurrence?.overrides;
  return Array.isArray(o) ? o.length : 0;
}

/** Ordinal for the day-of-month line: 1st, 2nd, 3rd, 21st… */
export function ordinal(n) {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
}

/**
 * The read-only line under the Repeat controls, derived from the DATE the user
 * has chosen rather than from separate pickers — a "start Monday, repeat
 * Tuesdays" combination has no sensible first occurrence, so the pattern day
 * is never entered directly.
 */
export function describeRepeatFromDate(freq, interval, dateMs, storedMonthDay = null) {
  const n = parseInt(dateMs, 10);
  if (Number.isNaN(n)) return "";
  const d = new Date(n);
  const every = clampInterval(interval);
  if (freq === FREQ_WEEKLY) {
    const name = WEEKDAY_NAMES[d.getDay()];
    return every === 1 ? `Every ${name}` : `Every ${every} weeks on ${name}`;
  }
  // A monthly occurrence can be SNAPPED — a "31st" series shows Feb 28 — so
  // while the date is the one the user tapped, the STORED day is the truth
  // (the server keeps byMonthDay on a same-day save, D11). Callers pass it
  // only while the date is unchanged; once the user picks another date the
  // server will take that day, and so does the preview. (2026-09-25, G8)
  const stored = parseInt(storedMonthDay, 10);
  const day = Number.isInteger(stored) && stored >= 1 && stored <= 31 ? stored : d.getDate();
  const base =
    every === 1
      ? `Monthly on the ${ordinal(day)}`
      : `Every ${every} months on the ${ordinal(day)}`;
  // Only mention the snap when it can actually fire.
  return day > 28 ? `${base} (the last day in shorter months)` : base;
}

/** Form state seeded from an existing item — `null` rule means "does not repeat". */
export function repeatStateFrom(calendarItem) {
  const r = calendarItem?.recurrence;
  if (!r?.freq) {
    return { on: false, freq: FREQ_WEEKLY, interval: "1", endsOn: "" };
  }
  return {
    on: true,
    freq: r.freq,
    interval: String(r.interval || "1"),
    endsOn: r.untilMs || "",
  };
}

/**
 * Form state → `RecurrenceInput`, or null when there is nothing to send.
 *
 * `""` on `untilMs` is the clearing sentinel, mirroring how `note` already
 * works: `null` would read as "not provided" and leave the old end date in
 * place with no way to remove it.
 */
export function repeatInputFrom(state, { hadRule }) {
  if (!state?.on) return hadRule ? { freq: FREQ_NONE } : null;
  return {
    freq: state.freq,
    interval: String(clampInterval(state.interval)),
    untilMs: state.endsOn ? String(state.endsOn) : "",
  };
}

/**
 * 1..52, the server's accepted range. `min`/`max` on a number input do not
 * constrain a TYPED value, so "-3" or "99" would otherwise reach the wire and
 * come back as a server rejection — and the preview line would meanwhile read
 * "Every -3 weeks".
 */
export function clampInterval(raw) {
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n)) return 1;
  return Math.min(52, Math.max(1, n));
}

/**
 * Warning to show BEFORE saving, or "" for none. A frequency change is the one
 * case where skipped days cannot be carried across, so they are dropped — the
 * user is told first, and the server reports the real count afterwards (this
 * count comes from a snapshot another admin can invalidate).
 */
export function repeatSaveWarning(item, state) {
  const r = item?.calendarItem?.recurrence;
  if (!r?.freq || !state?.on) return "";
  if (state.freq === r.freq) return "";
  const skipped = (r.skippedDayMs || []).length;
  const changing = state.freq === FREQ_MONTHLY ? "monthly" : "weekly";
  const dates = skipped === 1 ? "1 date" : `${skipped} dates`;
  return skipped > 0
    ? `Switching to ${changing} will bring back ${dates} you deleted from this series, and the series will restart from this event.`
    : `Switching to ${changing} will restart the series from this event — earlier events will no longer appear.`;
}
